<template>
  <div class="space-y-5">
    <h2 class="mb-5 text-center text-lg font-medium text-[var(--plugin-charcoal)]">
      {{ t('plugin.steps.customer') }}
    </h2>
    <form class="mx-auto max-w-[45rem] space-y-5" @submit.prevent="submit">
      <div>
        <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
          {{ t('plugin.customerStep.nameLabel') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          autocomplete="given-name"
          required
          :placeholder="t('plugin.customerStep.nameHint')"
          class="w-full rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-charcoal)] placeholder:text-[var(--plugin-gray-400)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div>
        <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
          {{ t('plugin.customerStep.surnameLabel') }}
        </label>
        <input
          v-model="form.surname"
          type="text"
          autocomplete="family-name"
          required
          :placeholder="t('plugin.customerStep.surnameHint')"
          class="w-full rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-charcoal)] placeholder:text-[var(--plugin-gray-400)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div>
        <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
          {{ t('plugin.customerStep.emailLabel') }}
        </label>
        <input
          v-model="form.email"
          type="email"
          autocomplete="email"
          required
          :placeholder="t('plugin.customerStep.emailHint')"
          :class="inputClass(!!(emailError && (emailTouched || submitted)))"
          @blur="emailTouched = true"
        />
        <p v-if="emailError && (emailTouched || submitted)" class="mt-1.5 text-sm text-red-600">
          {{ t(emailError) }}
        </p>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.customerStep.phoneLabel') }}
          </label>
          <input
            v-model="form.phone"
            type="tel"
            autocomplete="tel"
            required
            :placeholder="t('plugin.customerStep.phoneHint')"
            :class="inputClass(!!(phoneError && (phoneTouched || submitted)))"
            @blur="phoneTouched = true"
          />
          <p v-if="phoneError && (phoneTouched || submitted)" class="mt-1.5 text-sm text-red-600">
            {{ t(phoneError) }}
          </p>
        </div>
        <div>
          <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.customerStep.numberOfPeopleLabel') }}
          </label>
          <input
            v-model.number="form.numberOfPeople"
            type="number"
            :min="1"
            :max="maxPeople"
            class="w-full rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-charcoal)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p
            v-if="capacityInfo && capacityInfo.capacity > 1"
            class="mt-1.5 text-xs"
            :class="form.numberOfPeople > maxPeople ? 'text-red-600' : 'text-[var(--plugin-gray-500)]'"
          >
            {{ capacityInfo.available }}/{{ capacityInfo.capacity }} {{ t('plugin.customerStep.capacityAvailable') }}
          </p>
        </div>
      </div>
      <template v-if="isZlavaDna">
        <div>
          <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.customerStep.couponCodeLabel') }}
          </label>
          <input
            v-model="form.couponCode"
            type="text"
            :required="isZlavaDna"
            :placeholder="t('plugin.customerStep.couponCodeHint')"
            class="w-full rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-charcoal)] placeholder:text-[var(--plugin-gray-400)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
            {{ t('plugin.customerStep.couponPasswordLabel') }}
          </label>
          <input
            v-model="form.couponPassword"
            type="password"
            :required="isZlavaDna"
            :placeholder="t('plugin.customerStep.couponPasswordHint')"
            class="w-full rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-charcoal)] placeholder:text-[var(--plugin-gray-400)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <p
          v-if="state.couponVerified === false && state.couponVerificationError"
          class="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {{ state.couponVerificationError }}
        </p>
        <p
          v-else-if="state.couponVerified === false"
          class="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {{ t('plugin.customerStep.couponVerificationFailed') }}
        </p>
      </template>
      <div>
        <label class="mb-1.5 block text-sm font-medium text-[var(--plugin-charcoal)]">
          {{ t('plugin.customerStep.noteLabel') }}
        </label>
        <textarea
          v-model="form.note"
          rows="3"
          :placeholder="t('plugin.customerStep.noteHint')"
          class="w-full resize-y rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-charcoal)] placeholder:text-[var(--plugin-gray-400)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <slot name="actions" />
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch } from 'vue'
import { useWizardState, setCustomer } from '@/composables/useWizardState'
import { usePluginParams } from '@/composables/usePluginParams'
import { getCapacityInfo } from '@/api/public'
import type { CapacityInfoResponse, PublicReservationProduct } from '@/types/api'
import { t } from '@/i18n'
import { getEmailError, getPhoneError } from '@/utils/validators'

const params = usePluginParams()
const state = useWizardState()
const isZlavaDna = computed(() => params.value.source === 'zlavadna')
const form = reactive({
  name: '',
  surname: '',
  email: '',
  phone: '',
  note: '',
  numberOfPeople: 1,
  couponCode: '',
  couponPassword: '',
})

const emailTouched = ref(false)
const phoneTouched = ref(false)
const submitted = ref(false)

const emailError = computed(() => getEmailError(form.email))
const phoneError = computed(() => getPhoneError(form.phone))

const inputClass = (hasError: boolean) =>
  hasError
    ? 'w-full rounded-[8px] border border-red-500 bg-card p-4 text-[var(--plugin-on-bg)] placeholder:text-[var(--plugin-gray-400)] focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500'
    : 'w-full rounded-[8px] border border-[var(--plugin-border)] bg-card p-4 text-[var(--plugin-on-bg)] placeholder:text-[var(--plugin-gray-400)] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'

const capacityInfo = ref<CapacityInfoResponse | null>(null)

const maxPeople = computed(() => {
  const product = state.product as (PublicReservationProduct & { capacity?: number; available?: number }) | null
  if (state.product?.type === 'workshop' || (state.product as { is_workshop?: boolean })?.is_workshop) {
    const available = product?.available ?? product?.capacity ?? 999
    return Math.max(1, available)
  }
  const available = capacityInfo.value?.available ?? product?.available ?? product?.capacity ?? 999
  return Math.max(1, available)
})

watch(
  () => [
    state.branch?.id,
    state.product?.id,
    state.product?.type,
    state.selectedDate,
    state.selectedTime,
  ] as const,
  async ([branchId, , productType, selectedDate, selectedTime]) => {
    if (!branchId || !state.product || !selectedDate || !selectedTime) {
      capacityInfo.value = null
      return
    }
    if (productType === 'workshop') {
      capacityInfo.value = null
      return
    }
    try {
      const datetime = `${selectedDate} ${selectedTime.length >= 5 ? selectedTime.slice(0, 5) : selectedTime}`
      capacityInfo.value = await getCapacityInfo({
        branch_id: branchId,
        datetime,
        ...(productType === 'service' ? { service_id: state.product!.id } : { package_id: state.product!.id }),
      })
    } catch {
      capacityInfo.value = null
    }
  },
  { immediate: true }
)

watch(
  () => [form.numberOfPeople, maxPeople.value] as const,
  ([num, max]) => {
    const n = Number(num)
    const m = Number(max)
    if (!Number.isFinite(n) || n < 1) {
      form.numberOfPeople = 1
    } else if (n > m) {
      form.numberOfPeople = m
    }
  },
  { immediate: true }
)

watch(
  () => state.customer,
  (c) => {
    form.name = c.name
    form.surname = c.surname
    form.email = c.email
    form.phone = c.phone
    form.note = c.note
    form.couponCode = c.couponCode
    form.couponPassword = c.couponPassword
    const max = maxPeople.value
    form.numberOfPeople = Math.min(Math.max(1, c.numberOfPeople), max)
  },
  { immediate: true, deep: true }
)

watch(form, (f) => setCustomer(f), { deep: true })

function submit() {
  submitted.value = true
  setCustomer(form)
}
</script>
